// Notification des abonnés avec fusion des appels rapprochés : "la dernière demande est toujours honorée".
//
// POURQUOI. L'ancienne version de notifyListeners() ignorait tout appel arrivé moins de 50 ms après le précédent, SANS le
// reporter. L'état partagé changeait, mais aucun composant n'était réveillé : un clic sur le dock donné juste après une
// notification ne changeait pas l'écran (mesuré : 80 à 100 % de désaccord état / écran entre 5 et 45 ms, 0 % au-delà de
// 55 ms), et un `readOnly` rafraîchi restait invisible tant qu'aucune autre notification ne passait.
//
// STRATÉGIE : « premier appel immédiat, puis UN envoi différé par fenêtre ».
//   - après une période calme, l'appel s'exécute tout de suite (comportement inchangé pour un appel isolé) ;
//   - un appel arrivé dans la fenêtre de `minIntervalMs` programme UN envoi différé, à la fin de la fenêtre ;
//   - tous les appels suivants de la même fenêtre sont FUSIONNÉS dans cet envoi (au plus un envoi par fenêtre :
//     pas de tempête de rendus) ;
//   - un appel fait PENDANT l'exécution des abonnés (imbriqué) ne rappelle jamais les abonnés dans la même pile : la
//     fenêtre vient de s'ouvrir, il programme donc un envoi différé. Aucune récursion, aucune boucle synchrone.
// Garantie : après tout appel, les abonnés sont appelés AU MOINS UNE FOIS, au plus tard `minIntervalMs` plus tard,
// et toujours avec l'état le plus récent (ils le lisent au moment de l'envoi).
//
// L'horloge, les temporisateurs et le stockage de l'instant du dernier envoi sont injectables : le comportement est
// testé sans attente réelle (coalescedNotifier.test.js).

export const DEFAULT_MIN_INTERVAL_MS = 50

/**
 * @param {object} options
 * @param {() => void} options.run                 envoie la notification aux abonnés
 * @param {number} [options.minIntervalMs=50]      fenêtre minimale entre deux envois
 * @param {() => number} [options.now]             horloge (ms)
 * @param {(fn: Function, ms: number) => any} [options.setTimer]
 * @param {(handle: any) => void} [options.clearTimer]
 * @param {() => number} [options.getLastRun]      instant du dernier envoi (partagé, remis à 0 par un reset)
 * @param {(t: number) => void} [options.setLastRun]
 * @returns {(() => void) & { pending: () => boolean, cancel: () => void }}
 */
export function createCoalescedNotifier({
  run,
  minIntervalMs = DEFAULT_MIN_INTERVAL_MS,
  now = () => Date.now(),
  setTimer = (fn, ms) => setTimeout(fn, ms),
  clearTimer = (handle) => clearTimeout(handle),
  getLastRun,
  setLastRun,
}) {
  let lastRun = 0
  const readLastRun = getLastRun || (() => lastRun)
  const writeLastRun =
    setLastRun ||
    ((t) => {
      lastRun = t
    })
  let timer = null

  // La fenêtre est ouverte AVANT l'exécution : un appel imbriqué voit une fenêtre récente et se met en attente.
  const fire = () => {
    timer = null
    writeLastRun(now())
    run()
  }

  const notify = () => {
    if (timer !== null) return // un envoi différé est déjà programmé : cet appel y est fusionné
    // Borné par minIntervalMs : une horloge qui recule (ajustement système) ne doit pas différer indéfiniment.
    const wait = Math.min(minIntervalMs, minIntervalMs - (now() - readLastRun()))
    if (wait <= 0) {
      fire()
      return
    }
    timer = setTimer(fire, wait)
  }

  notify.pending = () => timer !== null
  notify.cancel = () => {
    if (timer !== null) {
      clearTimer(timer)
      timer = null
    }
  }
  return notify
}
